/**
 * Telling us what you found (D-607).
 *
 * <b>Nothing here asks who you are.</b> The server takes that from the session,
 * because a form that made a teacher type her name and address is a form she
 * abandons — and the one thing the app can always know is who is signed in.
 */

/** What kind of thing this is, in the words a tester would use. */
export type FeedbackKind = 'Problem' | 'Idea' | 'Request' | 'Support'

/**
 * The label beside each choice, which is doing most of the work.
 *
 * The words are the everyday ones rather than the ones a tracker would use:
 * nobody outside software files an "enhancement".
 */
export const FEEDBACK_KINDS: { value: FeedbackKind; label: string; hint: string }[] = [
  /*
   * <b>Every hint says a person will reply, because every one of them now does</b>
   * (D-838). The kind sorts the report; it no longer decides whether anybody
   * answers.
   *
   * ⚠ <b>Three of these used to promise nothing, and for one commit that was still
   * what they said while the code had stopped meaning it</b> — an audit walked the
   * path a parent takes: the dialog opens on *"Something is broken"*, whose hint
   * mentions no reply, she presses a button reading *Send*, and lands in a live
   * conversation with two people nothing had named. A control whose words do not
   * describe where it goes is the shape this project keeps having to fix.
   */
  {
    value: 'Problem',
    label: 'Something is broken',
    hint: 'It did the wrong thing, or nothing at all. We will reply here in the app.',
  },
  {
    value: 'Idea',
    label: 'Something could be better',
    hint: 'It works, but it is awkward or confusing. We will reply here in the app.',
  },
  {
    value: 'Request',
    label: 'Something is missing',
    hint: 'You wanted to do a thing the app cannot do. We will reply here in the app.',
  },
  {
    value: 'Support',
    label: 'I need help with something',
    hint: 'Anything else at all. We will reply here in the app.',
  },
]

export type FeedbackItem = {
  id: string
  kind: FeedbackKind
  body: string
  /** The screen she was on, so a defect names itself. */
  path: string | null
  build: string | null
  createdAt: string
  handledAt: string | null
  /** Resolved from her login rather than typed. */
  who: string
  email: string | null
  role: string
}

export async function sendFeedback(input: {
  kind: FeedbackKind
  body: string
  path: string
  build: string | null
}): Promise<void> {
  const res = await fetch('/api/feedback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.error ?? `That did not send (${res.status}). Please try again.`)
  }
}

/**
 * A support request, which makes a thread rather than a row (D-838).
 *
 * <b>Its own endpoint, inside the messaging module</b>, so it passes the gates
 * writing a conversation has to pass: whether this host has messaging at all,
 * whether this account may send (the one lever for silencing one person), the
 * unconsented-dancer refusal, and the create rate limit. `/api/feedback`
 * deliberately carries none of those, because sending a one-way note needs none.
 *
 * <b>It returns where to take her</b>, which is the half the owner asked for:
 * *"takes them to their messaging page so they see they now have a thread with
 * support."*
 */
export async function sendSupportRequest(input: {
  kind: FeedbackKind
  body: string
  path: string | null
  build: string | null
}): Promise<{ conversationId: string; created: boolean }> {
  const res = await fetch('/api/conversations/support', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)

    // The 503 names itself: nobody is on support, which is a studio that has not
    // been set up rather than anything she did.
    throw new Error(
      problem?.detail ?? problem?.error ?? `That did not send (${res.status}). Please try again.`,
    )
  }

  return res.json()
}

export async function fetchFeedback(outstanding: boolean): Promise<FeedbackItem[]> {
  const res = await fetch(`/api/admin/feedback?outstanding=${outstanding}`)
  if (!res.ok) throw new Error(`Could not load the feedback (${res.status})`)
  return res.json()
}

export async function markHandled(id: string, handled: boolean): Promise<void> {
  const res = await fetch(`/api/admin/feedback/${id}/handled?handled=${handled}`, { method: 'POST' })
  if (!res.ok) throw new Error(`Could not update that (${res.status})`)
}

/**
 * Which bundle the reader is running (D-520).
 *
 * Read off the page rather than asked for, so a report that arrives after a
 * deploy can be told apart from one about the version now live. Best effort:
 * a missing script tag is not worth failing a feedback form over.
 */
export function currentBuild(): string | null {
  const script = document.querySelector<HTMLScriptElement>('script[src*="/assets/index-"]')
  if (!script) return null

  return script.src.split('/').pop() ?? null
}
